import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PassFirstDashboard from './components/PassFirstDashboard';
import { AIExamGuard } from './components/AIExam/AIExamGuard';
import PracticeBridge from './components/PracticeBridge/PracticeBridge';
import { PracticeTest } from './components/PracticeTest/PracticeTest';
import { UserDashboard } from './components/UserDashboard/UserDashboard';
import ExamDateScreen from './components/ExamDateScreen/ExamDateScreen';
import SituationScreen from './components/SituationScreen/SituationScreen';
import { PracticePage } from './components/PracticePage/PracticePage';
import { VideoLessons } from './components/VideoLessons/VideoLessons';
import { SubscriptionPage } from './components/SubscriptionPage';
import { SubscriptionSuccessPage } from './components/SubscriptionSuccessPage';
import { AdvancedLayout } from './components/AdvancedLayout/AdvancedLayout';
import { AuthCallback } from './components/auth/AuthCallback';
import { SignupPage } from './components/auth/SignupPage';
import { LoginPage } from './components/auth/LoginPage';
import LanguageSelection from './components/LanguageSelection/LanguageSelection';
import TermsPage from './components/Legal/TermsPage';
import PrivacyPage from './components/Legal/PrivacyPage';
import LanguagePage from './components/Legal/LanguagePage';
import AccountPage from './components/Legal/AccountPage';
import FeedbackPage from './components/Legal/FeedbackPage';
import './App.css';
import { InstallPrompt } from './components/InstallPrompt';
import { IOSInstallPrompt } from './components/IOSInstallPrompt';
import { BrandedLoading } from './components/BrandedLoading';
import { THEORYCOACH_SUBSCRIPTION_SUCCESS_INSTALL } from './constants/theorycoachEvents';
import { startSession, endSession } from './services/dataCollectionService';
import { loadSubscriptionFromSupabase } from './services/subscriptionSyncService';
import { logger } from './utils/logger';
import { supabaseAuth } from './services/supabaseAuth';
import { userAuth } from './services/userAuth';

const isIOSSafari = (): boolean => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isWebKit = /WebKit/.test(ua);
  const isCriOS = /CriOS/.test(ua);
  const isFxiOS = /FxiOS/.test(ua);
  return isIOS && isWebKit && !isCriOS && !isFxiOS;
};

const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  !!(navigator as Navigator & { standalone?: boolean }).standalone;

const RootRouteEntry: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [resolved, setResolved] = useState<'checking' | 'authed' | 'guest'>('checking');

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      try {
        // Don't trust localStorage auth flags for routing:
        // stale flags can incorrectly redirect visitors away from marketing landing.
        const supaUser = await supabaseAuth.getCurrentUser();
        if (cancelled) return;

        if (supaUser) {
          setResolved('authed');
          return;
        }

        // No Supabase session: clear stale local cache so future visits show landing reliably.
        const localAuthenticated = localStorage.getItem('userAuthenticated') === 'true';
        const localUserId = localStorage.getItem('userId');
        if (localAuthenticated || localUserId) {
          localStorage.removeItem('userAuthenticated');
          localStorage.removeItem('username');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('userId');
          localStorage.removeItem('currentUser');
          userAuth.refreshUser();
          window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user: null } }));
        }

        setResolved('guest');
      } catch {
        if (!cancelled) setResolved('guest');
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasOAuthHash =
    location.pathname === '/' &&
    /(access_token|refresh_token|type)=/.test(location.hash);
  const hasOAuthSearchParams =
    location.pathname === '/' &&
    (
      searchParams.has('code') ||
      searchParams.has('token') ||
      searchParams.has('error') ||
      searchParams.has('error_description')
    );

  if (hasOAuthHash || hasOAuthSearchParams) {
    return (
      <Navigate
        to={{
          pathname: '/auth/callback',
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  if (resolved === 'checking') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          padding: '2rem',
          fontFamily: 'Inter, sans-serif'
        }}
      >
        <BrandedLoading context="general" />
      </div>
    );
  }

  if (resolved === 'authed') {
    return <Navigate to="/dashboard" replace />;
  }

  return <PassFirstDashboard />;
};

const DashboardRouteShell: React.FC = () => {
  const sessionStartedRef = useRef(false);

  // Keep dashboard fully page-owned (no global header/layout wrapper),
  // but preserve session tracking + subscription restore.
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;

    if (!sessionStartedRef.current) {
      sessionStartedRef.current = true;
      startSession(userId).catch(err => logger.warn('Session start failed:', err));
    }

    const subscriptionTier = localStorage.getItem('subscriptionTier');
    if (!subscriptionTier) {
      loadSubscriptionFromSupabase(userId).catch(err =>
        logger.warn('Subscription restore failed:', err)
      );
    }

    const handleBeforeUnload = () => {
      endSession(0, 0);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      endSession(0, 0);
    };
  }, []);

  return <UserDashboard />;
};

function App() {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const reconcileAuthSession = async () => {
      try {
        const localUserId = localStorage.getItem('userId');
        const localAuthenticated = localStorage.getItem('userAuthenticated') === 'true';

        // Fast path: nothing to reconcile.
        if (!localUserId && !localAuthenticated) return;

        const supaUser = await supabaseAuth.getCurrentUser();
        if (cancelled) return;

        // If local storage says "signed in" but Supabase has no session,
        // treat it as a stale/leftover session from a previous device user.
        if (!supaUser) {
          localStorage.removeItem('userAuthenticated');
          localStorage.removeItem('username');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('userId');
          localStorage.removeItem('currentUser');
          userAuth.refreshUser();
          window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user: null } }));
          return;
        }

        // If Supabase session exists, ensure local cache matches it.
        if (localUserId !== supaUser.id || !localAuthenticated) {
          const user = {
            id: supaUser.id,
            email: supaUser.email || '',
            name: supaUser.name || 'User',
            createdAt: new Date().toISOString(),
            isGuest: false
          };

          localStorage.setItem('userAuthenticated', 'true');
          localStorage.setItem('username', user.name);
          localStorage.setItem('userEmail', user.email);
          localStorage.setItem('userId', user.id);
          localStorage.setItem('currentUser', JSON.stringify(user));
          userAuth.refreshUser();
          window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user } }));
        }
      } catch (e) {
        logger.warn('Auth session reconcile failed:', e);
      }
    };

    reconcileAuthSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onSubscriptionSuccessInstall = () => {
      const dismissed = localStorage.getItem('installPromptDismissed');
      if (isStandalone()) return;
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        if (!Number.isNaN(dismissedTime)) {
          const daysSinceDismissed =
            (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
          if (daysSinceDismissed < 7) return;
        }
      }
      setTimeout(() => {
        if (isIOSSafari()) {
          setShowIOSPrompt(true);
        } else {
          setShowInstallPrompt(true);
        }
      }, 2000);
    };
    window.addEventListener(
      THEORYCOACH_SUBSCRIPTION_SUCCESS_INSTALL,
      onSubscriptionSuccessInstall
    );
    return () =>
      window.removeEventListener(
        THEORYCOACH_SUBSCRIPTION_SUCCESS_INSTALL,
        onSubscriptionSuccessInstall
      );
  }, []);

  return (
    <Router>
      <InstallPrompt
        show={showInstallPrompt}
        onDismiss={() => setShowInstallPrompt(false)}
      />
      <IOSInstallPrompt
        show={showIOSPrompt}
        onDismiss={() => setShowIOSPrompt(false)}
      />
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<RootRouteEntry />} />
        <Route path="/language" element={<LanguageSelection />} />
        <Route path="/situation" element={<SituationScreen />} />
        <Route path="/exam-date" element={<ExamDateScreen />} />
        <Route path="/dashboard" element={<DashboardRouteShell />} />
        <Route path="/tests" element={
          <AdvancedLayout>
            <PracticePage />
          </AdvancedLayout>
        } />
        <Route path="/practice" element={<Navigate to="/tests" replace />} />
        <Route path="/videos" element={
          <AdvancedLayout>
            <VideoLessons />
          </AdvancedLayout>
        } />
        <Route path="/ai-exam" element={
          <AdvancedLayout>
            <AIExamGuard />
          </AdvancedLayout>
        } />
        <Route path="/practice-bridge" element={<PracticeBridge />} />
        <Route path="/practice/:testId" element={
          <AdvancedLayout>
            <PracticeTest />
          </AdvancedLayout>
        } />
        <Route path="/subscription" element={
          <AdvancedLayout>
            <SubscriptionPage />
          </AdvancedLayout>
        } />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/settings/terms" element={<TermsPage />} />
        <Route path="/settings/privacy" element={<PrivacyPage />} />
        <Route path="/settings/language" element={<LanguagePage />} />
        <Route path="/settings/account" element={<AccountPage />} />
        <Route path="/settings/feedback" element={<FeedbackPage />} />
<Route path="/cookies" element={<Navigate to="/settings/privacy" replace />} />
      </Routes>
    </Router>
  );
}

export default App;